import {
  ButtonLink,
  cardStyles,
  Divider,
  FeaturedIcon,
  SectionWithHeader,
  SideDrawer,
  SidedrawerProps,
  Spec,
  SpecList,
  Typography,
} from '@maia/ui';
import { useDrawerStore } from '../../../stores/drawer.store';
import {
  IconAngleRight,
  IconCalendarAlt,
  IconHeartMedical,
  mapper,
} from '@maia/icons';
import { formatDate } from '@maia/utils';
import { twMerge } from 'tailwind-merge';
import { Link } from '@tanstack/react-router';

type Props = {} & Pick<SidedrawerProps, 'onOpenChange' | 'portalRef'>;

export function InsuranceSidedrawer(props: Props) {
  const { ...rest } = props;
  const { state, closeDrawer } = useDrawerStore();

  return (
    <SideDrawer
      {...rest}
      title="Bekijk verzekering"
      className="w-full pb-32 max-w-[var(--max-side-drawer-width)]"
      isOpen={state?.isOpen && state.info?.type === 'insurance'}
      onOpenChange={closeDrawer}
    >
      {state?.info?.type === 'insurance' ? (
        <div className="flex flex-col mt-32 gap-y-32">
          <div className="flex flex-col">
            <FeaturedIcon
              variant="light-square"
              size="sm"
              icon={mapper(state?.info.data.icon)}
            />

            <Typography
              variant="body-s"
              weight="medium"
              className="text-grayscale600 mt-12"
            >
              {state?.info.data.title}
            </Typography>

            <SpecList className="mt-12">
              <Spec
                className="grid grid-cols-2"
                title="Polisnummer"
                value={
                  <Link
                    to="/insurances/$guid"
                    params={{ guid: state.info.data.guid }}
                  >
                    <Typography
                      variant="body-xs"
                      weight="medium"
                      className="text-info300 text-nowrap"
                    >
                      {state.info.data.reference}
                    </Typography>
                  </Link>
                }
              />
            </SpecList>

            <Divider className="my-24" />

            <SectionWithHeader title="Details" titleSize="body-xs">
              <div
                className={twMerge(
                  cardStyles({ variant: 'outlined' }),
                  'p-12 flex gap-x-12',
                )}
              >
                <SpecList>
                  {state.info.data.coverages
                    .flatMap((coverage) => coverage.specs)
                    .map((spec, key) => (
                      <Spec
                        key={`insurance-sidedrawer-spec-${key}`}
                        className="grid grid-cols-2"
                        title={spec.label}
                        value={spec.value}
                      />
                    ))}
                </SpecList>
              </div>
            </SectionWithHeader>

            <SectionWithHeader
              title="Contract"
              titleSize="body-xs"
              className="mt-24"
            >
              <div
                className={twMerge(
                  cardStyles({ variant: 'outlined' }),
                  'p-12 flex gap-x-12',
                )}
              >
                <SpecList>
                  <Spec
                    icon={IconCalendarAlt}
                    className="grid grid-cols-2"
                    title="Ingangsdatum"
                    value={formatDate(state.info.data.effectiveDate)}
                  />
                  <Spec
                    icon={IconHeartMedical}
                    className="grid grid-cols-2"
                    title="Premievervaldatum"
                    value={formatDate(state.info.data.premiumDueDate)}
                  />
                </SpecList>
              </div>
            </SectionWithHeader>
          </div>

          <ButtonLink
            to="/insurances/$guid"
            params={{ guid: state.info.data.guid }}
            size="small"
            className="w-fit"
            iconRight={IconAngleRight}
          >
            Naar verzekering
          </ButtonLink>
        </div>
      ) : null}
    </SideDrawer>
  );
}
